import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { FilePlus2Icon, Undo2Icon } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { ApiError } from '@/api/errors'
import { TERMS_KEY, termsApi } from '@/api/terms'
import type { ClientTerms, Id } from '@/api/types'
import { useAuth } from '@/auth/use-auth'
import { useShellClient } from '@/components/clients/shell-client'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { TermsDiff } from '@/components/terms/terms-diff'
import { TermsDraft } from '@/components/terms/terms-draft'
import { TermsHistory } from '@/components/terms/terms-history'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

export const Route = createFileRoute('/_app/clients/$clientId/terms')({
  component: ClientTermsPage,
})

const REASONS: Record<string, string> = {
  terms_draft_exists: 'Un brouillon existe déjà : il est ci-dessous.',
  terms_not_published:
    'Aucune version globale n’est publiée : il n’y a rien à copier. Publiez d’abord les conditions globales.',
  forbidden: 'Seul un administrateur ramène un client aux conditions globales.',
}

const reasonOf = (error: unknown, fallback: string) =>
  error instanceof ApiError
    ? ((error.reason ? REASONS[error.reason] : undefined) ?? (error.message || fallback))
    : fallback

/** La phrase d'en-tête : ce qui s'applique aux prochaines soumissions du client. */
function headline(terms: ClientTerms): string {
  if (terms.mode === 'fork' && terms.current) {
    return `Version propre à ce client (version ${String(terms.current.version)}, copiée de la version globale ${String(terms.current.based_on_global_version ?? '—')})`
  }
  if (terms.global_version === null)
    return 'Aucune version globale publiée : aucune condition n’est jointe'
  return `Conditions globales (version ${String(terms.global_version)})`
}

/**
 * Les conditions générales d'un client (lot 42) : les globales, ou sa version
 * propre (un fork, copié du global). Quand la version globale avance, le fork
 * reste où il est : l'écran montre l'écart à reporter, le serveur ne fusionne
 * rien. « Revenir aux conditions globales » est un geste d'administrateur.
 */
function ClientTermsPage() {
  const client = useShellClient()
  const queryClient = useQueryClient()
  const { isAdmin } = useAuth()
  const [unforking, setUnforking] = useState(false)
  const terms = useQuery({
    queryKey: [...TERMS_KEY, 'client', client.id],
    queryFn: () => termsApi.client(client.id),
  })
  const refresh = () => queryClient.invalidateQueries({ queryKey: TERMS_KEY })
  const create = useMutation({
    mutationFn: () => termsApi.createDraft({ client_id: client.id }),
    onSuccess: () => {
      void refresh()
    },
  })
  const unfork = useMutation({
    mutationFn: () => termsApi.unfork(client.id),
    onSuccess: () => {
      setUnforking(false)
      void refresh()
      toast.success(`${client.name} reçoit de nouveau les conditions globales.`)
    },
    onError: () => {
      setUnforking(false)
    },
  })

  if (terms.isPending) {
    return (
      <Frame>
        <Skeleton className="h-32 w-full" />
      </Frame>
    )
  }
  if (terms.isError) {
    return (
      <Frame>
        <FormError>Les conditions de ce client n’ont pas pu être lues.</FormError>
      </Frame>
    )
  }
  const data = terms.data
  const forked = data.mode === 'fork'

  return (
    <div className="space-y-6" data-testid="conditions-client">
      <Frame>
        <section className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <h2 className="font-semibold tracking-tight">Conditions générales</h2>
              <p className="text-sm" data-testid="conditions-en-vigueur">
                {headline(data)}.
              </p>
              <p className="text-sm text-muted-foreground">
                Elles s’appliquent aux prochaines soumissions envoyées à {client.name} ; une
                soumission envoyée garde les conditions qu’elle porte.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {!data.draft && data.global_version !== null && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={create.isPending}
                  onClick={() => {
                    create.mutate()
                  }}
                >
                  <FilePlus2Icon />
                  {forked
                    ? 'Préparer une nouvelle version propre'
                    : 'Créer une version propre à ce client'}
                </Button>
              )}
              {forked && isAdmin && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setUnforking(true)
                  }}
                >
                  <Undo2Icon />
                  Revenir aux conditions globales
                </Button>
              )}
            </div>
          </div>
          {create.error && (
            <FormError>{reasonOf(create.error, 'Le brouillon n’a pas pu être créé.')}</FormError>
          )}
          {unfork.error && (
            <FormError>{reasonOf(unfork.error, 'Le retour n’a pas abouti.')}</FormError>
          )}

          {data.behind_global && data.current && data.global_version !== null && (
            <BehindGlobal
              from={data.current.based_on_global_version}
              to={data.global_version}
              hasDraft={data.draft !== null}
            />
          )}

          {data.draft && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium">
                Brouillon de la version propre {data.draft.version}
                {data.draft.based_on_global_version !== null &&
                  ` (copiée de la version globale ${String(data.draft.based_on_global_version)})`}
              </h3>
              <TermsDraft versionId={data.draft.id} clientId={client.id} />
            </div>
          )}
        </section>
      </Frame>

      {(forked || data.draft) && (
        <Frame>
          <TermsHistory scope={client.id} clientId={client.id} />
        </Frame>
      )}

      <ConfirmDialog
        open={unforking}
        onOpenChange={setUnforking}
        onConfirm={() => {
          unfork.mutate()
        }}
        title={`Revenir aux conditions globales pour ${client.name} ?`}
        description="Ses prochaines soumissions porteront les conditions globales. Ses versions propres restent lisibles, un brouillon en cours est jeté, et les soumissions déjà envoyées gardent leur texte."
        confirmLabel="Revenir aux conditions globales"
        pending={unfork.isPending}
      />
    </div>
  )
}

/**
 * Le fork est en retard : ce qui a changé dans le global depuis la version
 * copiée, pour le reporter à la main dans un nouveau brouillon du fork.
 */
function BehindGlobal({
  from,
  to,
  hasDraft,
}: {
  from: number | null
  to: number
  hasDraft: boolean
}) {
  const [open, setOpen] = useState(false)
  return (
    <div
      className="space-y-3 rounded-lg border border-warning/50 bg-warning/5 p-3 text-sm"
      data-testid="fork-en-retard"
    >
      <p>
        La version globale est passée de la version {from ?? '—'} à la version {to}. Cette version
        propre ne suit pas d’elle-même :{' '}
        {hasDraft
          ? 'reportez dans le brouillon ci-dessous ce qui doit l’être.'
          : 'préparez une nouvelle version propre, puis reportez ce qui doit l’être.'}
      </p>
      {from !== null && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            setOpen((current) => !current)
          }}
        >
          {open
            ? 'Masquer l’écart'
            : `Voir l’écart entre les versions globales ${String(from)} et ${String(to)}`}
        </Button>
      )}
      {open && from !== null && <GlobalGap from={from} to={to} />}
    </div>
  )
}

function GlobalGap({ from, to }: { from: number; to: number }) {
  const params = { client_id: 'global', page_size: 100 }
  const versions = useQuery({
    queryKey: [...TERMS_KEY, 'versions', params],
    queryFn: () => termsApi.versions(params),
  })
  const idOf = (version: number): Id | undefined =>
    versions.data?.items.find((item) => item.version === version)?.id
  const fromId = idOf(from)
  const toId = idOf(to)
  const left = useQuery({
    queryKey: [...TERMS_KEY, 'version', fromId],
    queryFn: () => termsApi.version(fromId ?? ''),
    enabled: fromId !== undefined,
  })
  const right = useQuery({
    queryKey: [...TERMS_KEY, 'version', toId],
    queryFn: () => termsApi.version(toId ?? ''),
    enabled: toId !== undefined,
  })
  if (versions.isError || left.isError || right.isError) {
    return <FormError>L’écart n’a pas pu être lu.</FormError>
  }
  if (!left.data || !right.data) return <Skeleton className="h-24 w-full" />
  return (
    <TermsDiff
      from={{ label: `la version globale ${String(from)}`, fr: left.data.fr, en: left.data.en }}
      to={{ label: `la version globale ${String(to)}`, fr: right.data.fr, en: right.data.en }}
    />
  )
}
